import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, User, Menu, Shield } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Button } from '../ui/button';

const navLinks = [
  { href: '/', label: 'Accueil' },
  { href: '/menu', label: 'Menu' },
  { href: '/a-propos', label: 'À Propos' },
  { href: '/galerie', label: 'Galerie' },
  { href: '/contact', label: 'Contact' },
  { href: '/reservation', label: 'Réserver' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { itemCount, openCart } = useCart();
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-warm-alabaster/95 backdrop-blur-md border-b border-border-subtle shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-2"
            data-testid="logo-link"
          >
            <span className="font-serif text-2xl font-semibold text-espresso tracking-tight">
              Léger's
            </span>
            <span className="font-serif text-2xl font-normal text-latte-gold">
              Coffee
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`nav-link relative ${
                  isActive(link.href) ? 'text-latte-gold' : ''
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-latte-gold" />
                )}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-4">
            {/* Admin button */}
            {!loading && isAuthenticated && isAdmin && (
              <Link
                to="/admin"
                data-testid="admin-link"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-soft-charcoal text-white hover:opacity-90 transition-colors duration-300"
              >
                <Shield className="w-4 h-4" />
                <span className="text-sm font-medium">Admin</span>
              </Link>
            )}

            {/* Account */}
            <Link
              to={isAuthenticated ? '/mon-compte' : '/connexion'}
              data-testid="account-link"
              className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full hover:bg-warm-secondary transition-colors duration-300"
            >
              <User className="w-5 h-5 text-soft-charcoal" />
            </Link>

            {/* Cart */}
            <button
              onClick={openCart}
              data-testid="cart-button"
              className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-warm-secondary transition-colors duration-300"
            >
              <ShoppingBag className="w-5 h-5 text-soft-charcoal" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-latte-gold text-warm-alabaster text-xs font-medium flex items-center justify-center rounded-full">
                  {itemCount}
                </span>
              )}
            </button>

            {/* Mobile Menu */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="mobile-menu-button"
                  className="w-10 h-10"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-warm-alabaster p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-border-subtle">
                    <span className="font-serif text-xl font-semibold text-espresso">
                      Léger's Coffee
                    </span>
                  </div>

                  <nav className="flex-1 p-6">
                    <ul className="space-y-4">
                      {navLinks.map((link) => (
                        <li key={link.href}>
                          <Link
                            to={link.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`block py-2 text-lg font-medium transition-colors duration-300 ${
                              isActive(link.href)
                                ? 'text-latte-gold'
                                : 'text-soft-charcoal hover:text-latte-gold'
                            }`}
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}

                      {!loading && isAuthenticated && isAdmin && (
                        <li>
                          <Link
                            to="/admin"
                            onClick={() => setIsMobileMenuOpen(false)}
                            data-testid="mobile-admin-link"
                            className="flex items-center gap-3 py-2 text-lg font-medium text-soft-charcoal hover:text-latte-gold transition-colors duration-300"
                          >
                            <Shield className="w-5 h-5" />
                            <span>Admin</span>
                          </Link>
                        </li>
                      )}
                    </ul>
                  </nav>

                  <div className="p-6 border-t border-border-subtle space-y-3">
                    <Link
                      to={isAuthenticated ? '/mon-compte' : '/connexion'}
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid="mobile-account-link"
                      className="flex items-center space-x-3 py-2 text-soft-charcoal hover:text-latte-gold transition-colors duration-300"
                    >
                      <User className="w-5 h-5" />
                      <span>{isAuthenticated ? 'Mon Compte' : 'Connexion'}</span>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;